import { createContext, createElement, useCallback, useContext, useMemo } from "react";
import { useLocation } from "react-router-dom";
import sr from "./sr";
import en from "./en";
import ru from "./ru";

// Site languages. Serbian is the default (no URL prefix); English and Russian live
// under /en and /ru so every language version has its own URL (needed for SEO / hreflang).
export const LANGS = {
  sr: { code: "sr", label: "SR", name: "Srpski", htmlLang: "sr-Latn", hreflang: "sr", ogLocale: "sr_RS", numberLocale: "sr-RS", prefix: "" },
  en: { code: "en", label: "EN", name: "English", htmlLang: "en", hreflang: "en", ogLocale: "en_US", numberLocale: "en-GB", prefix: "/en" },
  ru: { code: "ru", label: "RU", name: "Русский", htmlLang: "ru", hreflang: "ru", ogLocale: "ru_RU", numberLocale: "ru-RU", prefix: "/ru" },
};
export const LANG_CODES = Object.keys(LANGS);
export const DEFAULT_LANG = "sr";

const DICTIONARIES = { sr, en, ru };

// "/en/katalog" -> { lang: "en", path: "/katalog" }
export function splitPath(pathname = "/") {
  const match = pathname.match(/^\/(en|ru)(?=\/|$)/);
  if (!match) return { lang: DEFAULT_LANG, path: pathname || "/" };
  return { lang: match[1], path: pathname.slice(match[0].length) || "/" };
}

// ("/katalog", "en") -> "/en/katalog"; ("/#kontakt", "ru") -> "/ru#kontakt"
export function localizePath(path, lang) {
  const prefix = LANGS[lang]?.prefix || "";
  if (!prefix || /^(https?:|mailto:|tel:)/.test(path)) return path;
  if (path === "/" || path === "") return prefix;
  if (path.startsWith("/#") || path.startsWith("/?")) return prefix + path.slice(1);
  return prefix + path;
}

const lookup = (dict, key) => key.split(".").reduce((node, part) => (node == null ? node : node[part]), dict);

const interpolate = (text, vars) =>
  vars ? text.replace(/\{(\w+)\}/g, (m, name) => (vars[name] ?? vars[name] === 0 ? String(vars[name]) : m)) : text;

// Plurals: Serbian and Russian share the same three forms (1 / 2-4 / 5+), English has two.
export function pluralForm(lang, n) {
  const abs = Math.abs(n);
  if (lang === "en") return abs === 1 ? "one" : "many";
  const mod10 = abs % 10;
  const mod100 = abs % 100;
  if (mod10 === 1 && mod100 !== 11) return "one";
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return "few";
  return "many";
}

export function translate(lang, key, vars) {
  let value = lookup(DICTIONARIES[lang], key);
  if (value === undefined) value = lookup(DICTIONARIES[DEFAULT_LANG], key);
  if (value === undefined) {
    if (process.env.NODE_ENV !== "production") console.warn(`[i18n] missing key "${key}" (${lang})`);
    return key;
  }
  // { one, few, many } + vars.count -> the matching plural form
  if (value && typeof value === "object" && !Array.isArray(value) && vars && "count" in vars) {
    value = value[pluralForm(lang, vars.count)] ?? value.many ?? value.one;
  }
  return typeof value === "string" ? interpolate(value, vars) : value;
}

// Firestore fields may carry translations: informacije_o_zgradi_en, text_ru … (falls back to Serbian).
export const localized = (obj, field, lang) =>
  (lang !== DEFAULT_LANG && obj?.[`${field}_${lang}`]) || obj?.[field] || "";

const I18nContext = createContext(null);

export function I18nProvider({ children }) {
  const { pathname } = useLocation();
  const { lang, path } = splitPath(pathname);

  const t = useCallback((key, vars) => translate(lang, key, vars), [lang]);
  const value = useMemo(
    () => ({
      lang,
      meta: LANGS[lang],
      // Path without the language prefix, used to compare routes and to switch language.
      basePath: path,
      t,
      lp: (to) => localizePath(to, lang),
      num: (n, opts) => Number(n).toLocaleString(LANGS[lang].numberLocale, opts),
      field: (obj, name) => localized(obj, name, lang),
    }),
    [lang, path, t]
  );
  return createElement(I18nContext.Provider, { value }, children);
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used inside <I18nProvider>");
  return ctx;
}

export const useT = () => useI18n().t;

// Remember the choice so a later visit can suggest it. Never auto-redirect: crawlers and
// shared links always get the language from the URL.
export function rememberLang(lang) {
  try {
    localStorage.setItem("tehnocin.lang", lang);
  } catch {
    /* private mode, not important */
  }
}

export function preferredLang() {
  try {
    const saved = localStorage.getItem("tehnocin.lang");
    if (saved && LANGS[saved]) return saved;
  } catch {
    /* ignore */
  }
  const nav = (typeof navigator !== "undefined" && (navigator.languages || [navigator.language])) || [];
  for (const l of nav) {
    const code = String(l).slice(0, 2).toLowerCase();
    if (code === "ru" || code === "be" || code === "uk") return "ru";
    if (code === "sr" || code === "hr" || code === "bs" || code === "sh" || code === "me") return "sr";
    if (code === "en") return "en";
  }
  return DEFAULT_LANG;
}
